import React, { useEffect, useState } from 'react';
import { FeatureFlag } from '../../../../../../../utils/feature-flags/FeatureFlagsProvider';
import { Checkbox, Row } from 'antd';
import {
  FileTextOutlined,
  PicCenterOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import SvgAirplanemodeActive from '../../../../../../../components/icon-components/AirplanemodeActive';
import { useDispatch, useSelector } from 'react-redux';
import { getCommon, getCompany, getUserRole } from '../../../../../../../redux/selectors';
import styles from './TimelineSelector.module.scss';
import IconLabel from '../../../../../../../components/shared/IconLabel';
import GroupAirplaneModeActive from '../../../../../../../components/icons/GroupAirplaneModeActive.svg';
import Wrench from '../../../../../../../components/icons/Wrench.svg';
import {
  getSelectedTimelines,
  getVisibleTimelines,
} from '../../../redux-saga/selectors';
import { TimelineType } from '../../../types/timeline';
import { scheduleActions } from '../../../redux-saga/actions';
import { IDs, MAX_MOBILE_WIDTH } from '../../../../../../../constants';

export const TimelineSelector: React.FC = () => {
  const dispatch = useDispatch();
  const visibleTimelines = useSelector(getVisibleTimelines);
  const { featureFlags, isFeatureFlagsLoaded } = useSelector(getCommon);
  const selectedTimelines = useSelector(getSelectedTimelines);
  const company = useSelector(getCompany);
  const companySettings = company.Settings;
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const userRoles = useSelector(getUserRole);
  const maintenanceName = companySettings?.CompanyFieldNameMappings?.CrewScheduleMaintenance || 'Maintenance'
  const crewName = companySettings?.CompanyFieldNameMappings?.CrewSchedulePilot || 'Crew'


  const [isAllSelected, setIsAllSelected] = React.useState(false);

  useEffect(() => {
    const listener = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', listener);
    return () => window.removeEventListener('resize', listener);
  }, []);

  useEffect(() => {
    const expectedLength =
      (companySettings.AllowGroupSchedule ? 1 : 0) +
      (companySettings.MaintenanceSchedule ? 1 : 0) +
      (companySettings.ContractSchedule ? 1 : 0) +
      2; // For TimelineType.Aircraft, TimelineType.Maintenance and TimelineType.Crew

    setIsAllSelected(selectedTimelines.length === expectedLength);
  }, [
    selectedTimelines,
    companySettings.AllowGroupSchedule,
    companySettings.ContractSchedule,
    companySettings.MaintenanceSchedule,
  ]);

  if (!isFeatureFlagsLoaded) {
    return null;
  }

  if (visibleTimelines.length === 1 || !featureFlags[FeatureFlag.CollapsibleTimeline]) {
    return null;
  }

  const onTimelineChange = (values: TimelineType[], isAll = false) => {
    if (isAll) {
      const { AllowGroupSchedule, ContractSchedule, MaintenanceSchedule } = companySettings;
      values = [TimelineType.Aircraft, TimelineType.Crew];

      if (AllowGroupSchedule) {
        values.unshift(TimelineType.AircraftGroup);
      }

      if (MaintenanceSchedule) {
        values.unshift(TimelineType.Maintenance);
      }
      if (ContractSchedule) {
        values.push(TimelineType.Contract);
      }
    }

    dispatch(scheduleActions.setSelectedTimelines(values));
  };

  return (
    <Row
      style={{
        display: 'flex',
        flexDirection: 'row',
        marginBottom: windowWidth < MAX_MOBILE_WIDTH ? '1rem' : '',
      }}
    >
      <Checkbox.Group
        style={{ width: '100%' }}
        value={selectedTimelines}
        onChange={values => onTimelineChange(values as TimelineType[])}
      >
          <Checkbox
            id={IDs.filters.displayCrew}
            className={styles.TimelineSelector__checkbox}
            value={TimelineType.Crew}
          >
            <TeamOutlined /> {crewName}
          </Checkbox>
        <Checkbox
          id={IDs.filters.displayAircraft}
          className={styles.TimelineSelector__checkbox}
          value={TimelineType.Aircraft}
        >
          <SvgAirplanemodeActive />{' '}
          {companySettings?.CompanyFieldNameMappings?.CrewScheduleAircraft || 'Aircraft'}
        </Checkbox>
        {(companySettings?.ContractSchedule && !companySettings?.SalesModule) && (
          <Checkbox className={styles.TimelineSelector__checkbox} value={TimelineType.Contract}>
            <FileTextOutlined /> Contract
          </Checkbox>
        )}

        {(companySettings?.AllowGroupSchedule && companySettings?.SalesModule) && (
          <Checkbox
            className={styles.TimelineSelector__checkbox}
            value={TimelineType.AircraftGroup}
          >
            {/* @ts-ignore*/}
            <IconLabel icon={GroupAirplaneModeActive} text={''} />{' '}
            {companySettings?.SalesModule ? 'Base' : `${companySettings?.CompanyFieldNameMappings?.CrewScheduleAircraft || 'Aircraft'} Group`}
          </Checkbox>
        )}
        {companySettings?.MaintenanceSchedule && (
          <Checkbox className={styles.TimelineSelector__checkbox} value={TimelineType.Maintenance}>
            {/* @ts-ignore*/}
            <IconLabel icon={Wrench} text={''} /> {maintenanceName}
          </Checkbox>
        )}
        <Checkbox
          className={styles.TimelineSelector__checkbox}
          skipGroup
          checked={isAllSelected}
          disabled={isAllSelected}
          onClick={() => onTimelineChange([], true)}
        >
          <PicCenterOutlined /> All
        </Checkbox>
      </Checkbox.Group>
    </Row>
  );
};
